import { useEffect, useState } from 'react';
import { staffApi, formatApiError, viewProtectedFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtEdDate, fmtEdTime } from '@/lib/tz';
import { toast } from 'sonner';
import { ListChecks, PlusCircle, Trash2, Users, Check, CheckSquare, Type, Camera, Eye, Repeat, Clock } from 'lucide-react';

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const stamp = (d) => `${fmtEdDate(d, { month: 'short', day: 'numeric' })}, ${fmtEdTime(d)}`;
const blankItem = () => ({ label: '', requireCheck: true, requireText: false, requirePhoto: false, optional: false });

export default function Checklists() {
  const [checklists, setChecklists] = useState([]);
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'Operations', items: [blankItem()] });
  const [busy, setBusy] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [responsesFor, setResponsesFor] = useState(null);

  const load = () => staffApi.get('/checklists').then(({ data }) => setChecklists(data.checklists)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
    staffApi.get('/scheduling/staff').then(({ data }) => setStaff(data.staff)).catch(() => {});
    staffApi.get('/scheduling/positions').then(({ data }) => setRoles(data.positions)).catch(() => {});
  }, []);

  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const createChecklist = async () => {
    if (!form.title.trim()) return toast.error('Enter a checklist title');
    const items = form.items.filter((it) => it.label.trim());
    if (!items.length) return toast.error('Add at least one item');
    setBusy(true);
    try {
      await staffApi.post('/checklists', { ...form, items });
      toast.success('Checklist created');
      setCreateOpen(false);
      setForm({ title: '', description: '', category: 'Operations', items: [blankItem()] });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeChecklist = async (id) => {
    if (!window.confirm('Delete this checklist and its assignments?')) return;
    try { await staffApi.delete(`/checklists/${id}`); toast.success('Deleted'); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-700 text-charcoal flex items-center gap-2"><ListChecks size={22} className="text-saffron" /> Checklists</h1>
        <Button data-testid="checklist-create-open-btn" onClick={() => setCreateOpen(true)} className="bg-saffron hover:bg-saffron-hover gap-1.5"><PlusCircle size={16} /> New Checklist</Button>
      </div>

      {checklists.length === 0 ? (
        <div className="bg-white rounded-xl border border-border/40 p-6 text-center text-sm text-muted-ink">No checklists yet. Build an opening/closing checklist and assign it to your team.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {checklists.map((c) => (
            <div key={c.id} className="bg-white rounded-xl border border-border/40 p-4" data-testid="checklist-card">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading font-700 text-charcoal">{c.title}</p>
                  {c.description && <p className="text-xs text-muted-ink mt-0.5">{c.description}</p>}
                  <p className="text-xs text-muted-ink mt-1">{c.items.length} items · {c._count.assignments} assigned</p>
                </div>
                <button data-testid="checklist-delete-btn" onClick={() => removeChecklist(c.id)} className="text-destructive shrink-0"><Trash2 size={16} /></button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {c.items.slice(0, 6).map((it) => (
                  <span key={it.id} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-bone text-muted-ink">
                    {it.requireCheck && <CheckSquare size={10} />}{it.requireText && <Type size={10} />}{it.requirePhoto && <Camera size={10} />}
                    {it.label}
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Button data-testid="checklist-assign-btn" size="sm" variant="outline" onClick={() => setAssignFor(c)} className="gap-1.5"><Users size={14} /> Assign</Button>
                <Button data-testid="checklist-responses-btn" size="sm" variant="ghost" onClick={() => setResponsesFor(c)} className="gap-1.5 text-coriander"><Eye size={14} /> Responses</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="checklist-create-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>New Checklist</DialogTitle><DialogDescription>Build the items and choose what response each one needs.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="checklist-title-input" placeholder="Checklist title (e.g. Opening Checklist)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea data-testid="checklist-desc-input" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="space-y-2">
              <Label>Items</Label>
              {form.items.map((it, i) => (
                <div key={i} className="rounded-lg border border-border/40 p-2.5 space-y-2" data-testid="checklist-item-row">
                  <div className="flex gap-2">
                    <Input data-testid={`checklist-item-label-${i}`} placeholder={`Item ${i + 1}`} value={it.label} onChange={(e) => setItem(i, { label: e.target.value })} />
                    {form.items.length > 1 && <button onClick={() => removeItem(i)} className="text-destructive shrink-0"><Trash2 size={15} /></button>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[['requireCheck', 'Checkmark', CheckSquare], ['requireText', 'Note', Type], ['requirePhoto', 'Photo', Camera]].map(([k, label, Icon]) => (
                      <button key={k} type="button" data-testid={`checklist-item-${k}-${i}`} onClick={() => setItem(i, { [k]: !it[k] })}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${it[k] ? 'bg-saffron text-white border-saffron' : 'bg-white text-charcoal border-border/60'}`}>
                        <Icon size={12} /> {label}
                      </button>
                    ))}
                    <button type="button" onClick={() => setItem(i, { optional: !it.optional })}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${it.optional ? 'bg-muted-ink/20 text-charcoal border-muted-ink/30' : 'bg-white text-muted-ink border-border/60'}`}>
                      {it.optional ? 'Optional' : 'Required'}
                    </button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" data-testid="checklist-add-item-btn" onClick={addItem} className="gap-1.5"><PlusCircle size={14} /> Add item</Button>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="checklist-create-submit-btn" disabled={busy} onClick={createChecklist} className="bg-saffron hover:bg-saffron-hover">{busy ? 'Saving…' : 'Create Checklist'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {assignFor && <AssignDialog checklist={assignFor} staff={staff} roles={roles} onClose={() => setAssignFor(null)} onDone={load} />}
      {responsesFor && <ResponsesDialog checklist={responsesFor} onClose={() => setResponsesFor(null)} />}
    </div>
  );
}

function AssignDialog({ checklist, staff, roles, onClose, onDone }) {
  const [pickedStaff, setPickedStaff] = useState([]);
  const [pickedRoles, setPickedRoles] = useState([]);
  const [recurrence, setRecurrence] = useState('DAILY');
  const [weekdays, setWeekdays] = useState([1, 2, 3, 4, 5]);
  const [busy, setBusy] = useState(false);
  const active = staff;

  const recipients = new Set(pickedStaff);
  if (pickedRoles.length) for (const s of active) if ((s.jobRoles || s.positions || []).some((r) => pickedRoles.includes(r.id))) recipients.add(s.id);

  const submit = async () => {
    if (recipients.size === 0) return toast.error('Select staff or a role');
    setBusy(true);
    try {
      const { data } = await staffApi.post(`/checklists/${checklist.id}/assign`, {
        staffIds: pickedStaff, roleIds: pickedRoles, recurrence,
        weekdays: recurrence === 'ONCE' ? [] : weekdays,
      });
      toast.success(`Assigned to ${data.count} staff`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checklist-assign-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Assign: {checklist.title}</DialogTitle><DialogDescription>Choose who does it and how often.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Repeats</Label>
            <Select value={recurrence} onValueChange={setRecurrence}>
              <SelectTrigger data-testid="assign-recurrence-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ONCE">One-time</SelectItem>
                <SelectItem value="DAILY">Every day</SelectItem>
                <SelectItem value="WEEKLY">Selected weekdays</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {recurrence !== 'ONCE' && (
            <div className="flex flex-wrap gap-1.5">
              {WD.map((d, i) => (
                <button key={d} type="button" data-testid={`assign-weekday-${i}`} onClick={() => setWeekdays((w) => (w.includes(i) ? w.filter((x) => x !== i) : [...w, i]))}
                  className={`px-2.5 py-1 rounded-full text-xs border ${weekdays.includes(i) ? 'bg-saffron text-white border-saffron' : 'bg-white text-charcoal border-border/60'}`}>{d}</button>
              ))}
            </div>
          )}
          <div>
            <p className="text-xs text-muted-ink mb-1">By role</p>
            <div className="flex flex-wrap gap-1.5">
              {roles.map((r) => (
                <button key={r.id} type="button" data-testid={`assign-role-${r.id}`} onClick={() => setPickedRoles((p) => (p.includes(r.id) ? p.filter((x) => x !== r.id) : [...p, r.id]))}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${pickedRoles.includes(r.id) ? 'bg-saffron text-white border-saffron' : 'bg-white text-charcoal border-border/60'}`}>
                  {pickedRoles.includes(r.id) && <Check size={12} />}{r.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-ink mb-1">Individual staff — {recipients.size} selected</p>
            <div className="max-h-40 overflow-y-auto rounded-lg border border-border/40 divide-y divide-border/30">
              {active.map((s) => {
                const viaRole = pickedRoles.length && (s.jobRoles || s.positions || []).some((r) => pickedRoles.includes(r.id));
                return (
                  <label key={s.id} data-testid={`assign-staff-${s.id}`} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${viaRole ? 'opacity-70' : ''}`}>
                    <input type="checkbox" checked={pickedStaff.includes(s.id) || viaRole} disabled={viaRole} onChange={() => setPickedStaff((p) => (p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id]))} className="h-4 w-4 accent-saffron" />
                    <span className="text-charcoal">{s.name}</span>
                    {viaRole && <span className="text-[10px] text-saffron ml-auto">via role</span>}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="assign-submit-btn" disabled={busy || recipients.size === 0} onClick={submit} className="bg-saffron hover:bg-saffron-hover">{busy ? 'Assigning…' : `Assign to ${recipients.size}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResponsesDialog({ checklist, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    staffApi.get(`/checklists/${checklist.id}/completions`).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, [checklist.id]);
  const items = data?.checklist?.items || [];
  const labelOf = Object.fromEntries(items.map((it) => [it.id, it.label]));
  const viewPhoto = (compId, itemId) => viewProtectedFile(staffApi, `/checklists/completions/${compId}/photo/${itemId}`).catch((e) => toast.error(formatApiError(e)));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checklist-responses-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Responses: {checklist.title}</DialogTitle><DialogDescription>Latest completions from your team.</DialogDescription></DialogHeader>
        {!data ? <p className="text-sm text-muted-ink">Loading…</p> : data.completions.length === 0 ? (
          <p className="text-sm text-muted-ink">No completions yet.</p>
        ) : (
          <div className="space-y-3">
            {data.completions.map((c) => (
              <div key={c.id} className="rounded-lg border border-border/40 p-3" data-testid="completion-row">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-charcoal">{c.staffName}</p>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.status === 'COMPLETED' ? 'bg-coriander/15 text-coriander' : 'bg-amber-100 text-amber-700'}`}>{c.status === 'COMPLETED' ? 'Completed' : 'In progress'}</span>
                </div>
                <p className="text-[11px] text-muted-ink flex items-center gap-1"><Clock size={11} /> Started {stamp(c.startedAt)}{c.completedAt ? ` · Done ${stamp(c.completedAt)}` : ''}</p>
                <div className="mt-2 space-y-1">
                  {(c.responses || []).map((r) => (
                    <div key={r.itemId} className="text-xs flex items-start gap-2">
                      <span className={r.checked ? 'text-coriander' : 'text-muted-ink'}>{r.checked ? '✓' : '•'}</span>
                      <div className="min-w-0">
                        <span className="text-charcoal">{labelOf[r.itemId] || 'Item'}</span>
                        {r.text && <span className="text-muted-ink"> — {r.text}</span>}
                        {r.photoPath && <button onClick={() => viewPhoto(c.id, r.itemId)} className="text-saffron ml-1 underline">photo</button>}
                        <span className="text-muted-ink/60"> · {fmtEdTime(r.respondedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
